import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useFleetApi, type TaskRow } from "./api";

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function statusPill(status: string): "ok" | "warn" | "crit" | "muted" {
  if (status === "resolved") return "ok";
  if (status === "pending" || status === "running") return "warn";
  if (status === "failed") return "crit";
  return "muted";
}

const STATUSES = ["all", "pending", "running", "resolved", "failed"];

export default function Tasks() {
  const { token, logout } = useAuth();
  const { fetchTasksRecent } = useFleetApi(token, logout);
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "all";

  const [rows, setRows] = useState<TaskRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    fetchTasksRecent({ status: status === "all" ? undefined : status, limit: 200 })
      .then(r => { if (alive) setRows(r); })
      .catch(e => { if (alive) setErr(String(e)); });
    return () => { alive = false; };
  }, [fetchTasksRecent, status]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    if (rows) for (const r of rows) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [rows]);

  return (
    <section>
      <p className="page-lede">
        {rows == null ? "Loading tasks…" : `${rows.length} shown.`}{" "}
        Background work the runtime has queued or run — each row is one{" "}
        <code>fleet.task</code> (pending, in flight, resolved, or failed).
      </p>

      <div className="chips">
        {STATUSES.map(s => (
          <span
            key={s}
            className={`chip${status === s ? " active" : ""}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (s === "all") next.delete("status"); else next.set("status", s);
              setParams(next, { replace: true });
            }}
          >
            {s}
            {s !== "all" && counts[s] != null && ` · ${counts[s]}`}
          </span>
        ))}
      </div>

      {err && <p style={{ color: "var(--crit)", fontSize: "12.5px" }}>{err}</p>}

      <div className="card">
        <div
          className="feed"
          style={{ gridTemplateColumns: "auto 1fr 1fr auto auto" }}
        >
          <div className="col">ID</div>
          <div className="col">Caller → callee</div>
          <div className="col">Status / failure</div>
          <div className="col">Created</div>
          <div className="col">Resolved</div>

          {rows == null && (
            <div className="col" style={{ gridColumn: "1 / -1", color: "var(--text-3)", padding: "12px 0" }}>
              Loading…
            </div>
          )}
          {rows && rows.length === 0 && (
            <div className="col" style={{ gridColumn: "1 / -1", color: "var(--text-3)", padding: "12px 0" }}>
              No tasks match this filter.
            </div>
          )}

          {rows?.map(r => (
            <Fragment key={r.id}>
              <div className="col t" style={{ fontVariantNumeric: "tabular-nums" }}>#{r.id}</div>
              <div className="col body">
                <code>{r.caller}</code> → <code>{r.callee}</code>
                {r.trigger && <span style={{ color: "var(--text-3)", marginLeft: "6px" }}>· {r.trigger}</span>}
              </div>
              <div
                className="col body"
                style={{ cursor: r.failure_reason ? "pointer" : "default" }}
                onClick={() => r.failure_reason && setExpanded(x => (x === r.id ? null : r.id))}
              >
                <span className={`pill ${statusPill(r.status)}`}>{r.status}</span>
                {r.failure_reason && (
                  <span style={{ color: "var(--text-2)", marginLeft: "6px", fontSize: "12px" }}>
                    {expanded === r.id
                      ? r.failure_reason
                      : r.failure_reason.length > 70
                        ? r.failure_reason.slice(0, 70) + "…"
                        : r.failure_reason}
                  </span>
                )}
              </div>
              <div className="col t">{fmtTime(r.created_at)}</div>
              <div className="col t">{fmtTime(r.resolved_at)}</div>
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
